import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import ContactForm from '../components/ContactForm';
import { getOptimizedImageUrl } from '../lib/cms';
import SEO from '../components/SEO';

const BOOKING_URL = 'https://cryobybreum.planway.com/';

export default function Contact() {
  return (
    <div className="animate-in fade-in duration-200">
      <SEO preset="contact" />
      {/* 1. HERO */}
      <section className="py-16 sm:py-20 bg-sand-light/60 border-b border-taupe-border/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center max-w-3xl">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-xs text-taupe mb-6" aria-label="Brødkrummesti">
            <Link to="/" className="hover:text-espresso transition-colors">
              Forside
            </Link>
            <span className="text-taupe-border">/</span>
            <span className="text-espresso font-medium">Kontakt</span>
          </nav>

          <h1 className="font-sans text-3xl sm:text-4xl lg:text-5xl font-semibold text-espresso uppercase tracking-wider">
            Kontakt & Find vej
          </h1>

          <p className="text-base sm:text-lg text-taupe mt-4 leading-relaxed">
            Har du spørgsmål til vores kropsbehandlinger, eller vil du bestille tid hos{' '}
            <Link to="/" className="text-espresso font-semibold hover:text-accent-brown underline underline-offset-4 decoration-accent-brown/50 hover:decoration-accent-brown transition-colors">
              Cryo by Breum
            </Link>
            ? Vi sidder klar til at hjælpe dig.
          </p>
        </div>
      </section>

      {/* 2. CONTACT INFO & FORM SECTION */}
      <section className="py-20 bg-white border-y border-taupe-border/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left Column: Contact Cards */}
          <div className="lg:col-span-5 space-y-6 reveal-on-scroll">
            <div>
              <h2 className="font-sans text-2xl sm:text-3xl font-semibold text-espresso uppercase tracking-wider">
                Cryo by Breum
              </h2>
              <p className="text-sm text-taupe mt-2 leading-relaxed">
                Klinik for{' '}
                <Link to="/behandlinger/fedtfrysning" className="text-espresso font-medium hover:text-accent-brown underline underline-offset-4 decoration-accent-brown/40 hover:decoration-accent-brown transition-colors">
                  fedtfrysning
                </Link>
                ,{' '}
                <Link to="/behandlinger/ems-traening" className="text-espresso font-medium hover:text-accent-brown underline underline-offset-4 decoration-accent-brown/40 hover:decoration-accent-brown transition-colors">
                  EMS træning
                </Link>{' '}
                og{' '}
                <Link to="/behandlinger/body-roll" className="text-espresso font-medium hover:text-accent-brown underline underline-offset-4 decoration-accent-brown/40 hover:decoration-accent-brown transition-colors">
                  Body Roll
                </Link>{' '}
                i Odense SV. Se også vores{' '}
                <Link to="/priser" className="text-espresso font-medium hover:text-accent-brown underline underline-offset-4 decoration-accent-brown/40 hover:decoration-accent-brown transition-colors">
                  priser
                </Link>{' '}
                eller læs mere{' '}
                <Link to="/om-cryo-by-breum" className="text-espresso font-medium hover:text-accent-brown underline underline-offset-4 decoration-accent-brown/40 hover:decoration-accent-brown transition-colors">
                  om klinikken
                </Link>
                .
              </p>
            </div>

            {/* Address */}
            <div className="flex items-start gap-4 py-2">
              <div className="text-espresso text-xl shrink-0 mt-0.5">
                <i className="fa-solid fa-location-dot" aria-hidden="true" />
              </div>
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-taupe">Adresse</h3>
                <p className="text-base font-semibold text-espresso mt-0.5">Dalumvej 82</p>
                <p className="text-sm text-espresso-light">5250 Odense SV</p>
                <p className="text-xs text-taupe mt-2">Gode parkeringsforhold lige ved klinikken.</p>
              </div>
            </div>

            {/* Phone */}
            <div className="flex items-start gap-4 py-2">
              <div className="text-espresso text-xl shrink-0 mt-0.5">
                <i className="fa-solid fa-phone" aria-hidden="true" />
              </div>
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-taupe">Telefon</h3>
                <a
                  href="tel:+4526434402"
                  className="text-base font-semibold text-espresso hover:text-accent-brown transition-colors whitespace-nowrap block mt-0.5"
                >
                  26 43 44 02
                </a>
                <p className="text-xs text-taupe mt-1">Ring ved spørgsmål eller skræddersyede forløb.</p>
              </div>
            </div>

            {/* Opening Hours */}
            <div className="flex items-start gap-4 py-2">
              <div className="text-espresso text-xl shrink-0 mt-0.5">
                <i className="fa-regular fa-clock" aria-hidden="true" />
              </div>
              <div className="w-full max-w-xs">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-taupe">Åbningstider</h3>
                <div className="mt-2 space-y-1.5 text-sm">
                  <div className="flex justify-between border-b border-taupe-border/40 pb-1">
                    <span className="text-taupe">Mandag:</span>
                    <span className="font-semibold text-espresso">12:00 – 19:00</span>
                  </div>
                  <div className="flex justify-between border-b border-taupe-border/40 pb-1">
                    <span className="text-taupe">Tirsdag:</span>
                    <span className="font-semibold text-espresso">11:00 – 22:00</span>
                  </div>
                  <div className="flex justify-between border-b border-taupe-border/40 pb-1">
                    <span className="text-taupe">Onsdag:</span>
                    <span className="font-semibold text-espresso">10:00 – 20:00</span>
                  </div>
                  <div className="flex justify-between border-b border-taupe-border/40 pb-1">
                    <span className="text-taupe">Torsdag:</span>
                    <span className="font-semibold text-espresso">10:00 – 18:30</span>
                  </div>
                  <div className="flex justify-between pb-1">
                    <span className="text-taupe">Fredag:</span>
                    <span className="font-semibold text-espresso text-right">
                      10:00 – 16:00
                      <span className="block text-xs font-normal text-taupe">(i ulige uger)</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Online Booking */}
            <div className="flex items-start gap-4 py-2">
              <div className="text-espresso text-xl shrink-0 mt-0.5">
                <i className="fa-solid fa-calendar-check" aria-hidden="true" />
              </div>
              <div className="space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-taupe">Online Booking</h3>
                <p className="text-sm text-espresso-light">
                  Den hurtigste måde at reservere tid til din næste behandling.
                </p>
                <div className="pt-1">
                  <a
                    href={BOOKING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-accent-brown text-white text-xs font-semibold tracking-widest uppercase hover:bg-accent-brown-hover transition-colors shadow-xs"
                  >
                    <span>Åbn Planway kalender</span>
                    <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Webform */}
          <div className="lg:col-span-7 reveal-on-scroll delay-150">
            <ContactForm />
          </div>
        </div>
      </div>
    </section>
    </div>
  );
}
